import React, { useState, useEffect, useCallback } from 'react';
import { serviceRequestAPI, customerAPI, broadbandAPI, catvAPI, gtplSaathiAPI } from '../services/api';
import { customerTypeLabel } from '../utils/customerTypeLabel';
import SearchableSelect from '../components/common/SearchableSelect';
import { format, addDays, addMonths, startOfMonth, startOfDay } from 'date-fns';
import toast from 'react-hot-toast';
import { Plus, Search, ClipboardList, CheckCircle, XCircle, Clock, PowerOff, Power, Repeat, MapPin, Pencil } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { useViewMode } from '../hooks/useViewMode';

export const TYPE_LABEL = {
  disconnection: 'Disconnection', reconnection: 'Reconnection',
  package_change: 'Package Change', relocation: 'Relocation',
};
export const TYPE_ICON = { disconnection: PowerOff, reconnection: Power, package_change: Repeat, relocation: MapPin };
export const STATUS_BADGE = { pending: 'pending', scheduled: 'warning', completed: 'resolved', rejected: 'danger', superseded: 'muted' };
const DISCONNECTION_REASONS = [
  'JIO', 'Airtel', 'DTH', 'GTPL Broadband', 'AIS Broadband', 'Shifting', 'Vacation',
  'Studies & Exam', 'TV Issue', 'Temporary', 'Going out of station/India',
];

function NewRequestModal({ onClose, onSuccess }) {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({ type: 'disconnection', serviceType: 'broadband', reason: '', requestedPlan: '', requestedPackage: '' });
  const [newAddress, setNewAddress] = useState({ line1: '', area: '', city: '', pincode: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (search.length >= 2) {
      customerAPI.getAll({ search, limit: 10 }).then(({ data }) => setCustomers(data.data)).catch(() => {});
    }
  }, [search]);

  useEffect(() => {
    if (form.type === 'package_change' && form.serviceType === 'broadband' && plans.length === 0) {
      broadbandAPI.getPlans({ activeOnly: 'true', limit: 200 }).then(r => setPlans(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
    }
    if (form.type === 'package_change' && form.serviceType === 'catv' && packages.length === 0) {
      catvAPI.getPackages({ activeOnly: 'true', limit: 200 }).then(r => setPackages(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
    }
  }, [form.type, form.serviceType, plans.length, packages.length]);

  const serviceOptions = !selectedCustomer ? ['broadband', 'catv']
    : selectedCustomer.customerType === 'both' ? ['broadband', 'catv']
    : [selectedCustomer.customerType];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) { toast.error('Select a customer'); return; }
    setLoading(true);
    try {
      const payload = { customer: selectedCustomer._id, type: form.type, reason: form.reason };
      if (['disconnection', 'reconnection', 'package_change'].includes(form.type)) {
        payload.serviceType = form.serviceType;
      }
      if (form.type === 'package_change') {
        if (form.serviceType === 'broadband') payload.requestedPlan = form.requestedPlan;
        else payload.requestedPackage = form.requestedPackage;
      }
      if (form.type === 'relocation') payload.newAddress = newAddress;

      const { data } = await serviceRequestAPI.create(payload);
      toast.success(`Request ${data.data.ticketNumber} submitted`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>New Service Request</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Customer <span className="required">*</span></label>
              <input className="form-control" placeholder="Search customer by name / mobile..."
                value={search} onChange={e => { setSearch(e.target.value); setSelectedCustomer(null); }} />
              {customers.length > 0 && !selectedCustomer && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 4, maxHeight: 150, overflowY: 'auto' }}>
                  {customers.map(c => (
                    <div key={c._id} onClick={() => { setSelectedCustomer(c); setSearch(`${c.name} (${c.customerId})`); setCustomers([]); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '.85rem', borderBottom: '1px solid var(--border)' }}
                      onMouseOver={e => e.currentTarget.style.background = 'var(--bg)'}
                      onMouseOut={e => e.currentTarget.style.background = 'white'}>
                      <strong>{c.name}</strong> · {c.customerId} · {c.mobile} · <span>{customerTypeLabel(c.customerType)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Request Type</label>
              <select className="form-control" value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value, requestedPlan: '', requestedPackage: '', reason: '' }))}>
                <option value="disconnection">Disconnection</option>
                <option value="reconnection">Reconnection</option>
                <option value="package_change">Package / Plan Change</option>
                <option value="relocation">Relocation</option>
              </select>
            </div>

            {['disconnection', 'reconnection', 'package_change'].includes(form.type) && (
              <div className="form-group">
                <label className="form-label">Service</label>
                <select className="form-control" value={form.serviceType}
                  onChange={e => setForm(f => ({ ...f, serviceType: e.target.value, requestedPlan: '', requestedPackage: '' }))}>
                  {serviceOptions.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s === 'broadband' ? 'Broadband' : 'CableTV'}</option>)}
                </select>
              </div>
            )}

            {form.type === 'package_change' && form.serviceType === 'broadband' && (
              <div className="form-group">
                <label className="form-label">Requested Plan <span className="required">*</span></label>
                <SearchableSelect
                  value={form.requestedPlan}
                  onChange={v => setForm(f => ({ ...f, requestedPlan: v }))}
                  placeholder="Search plan…"
                  options={plans.map(p => ({ value: p._id, label: `${p.name} — ₹${p.price}` }))}
                />
              </div>
            )}
            {form.type === 'package_change' && form.serviceType === 'catv' && (
              <div className="form-group">
                <label className="form-label">Requested Package <span className="required">*</span></label>
                <SearchableSelect
                  value={form.requestedPackage}
                  onChange={v => setForm(f => ({ ...f, requestedPackage: v }))}
                  placeholder="Search package…"
                  options={packages.map(p => ({ value: p._id, label: `${p.displayName || p.name} — ₹${p.price}` }))}
                />
              </div>
            )}

            {form.type === 'relocation' && (
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">New Address <span className="required">*</span></label>
                  <input className="form-control" value={newAddress.line1} onChange={e => setNewAddress(a => ({ ...a, line1: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Area</label>
                  <input className="form-control" value={newAddress.area} onChange={e => setNewAddress(a => ({ ...a, area: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input className="form-control" value={newAddress.city} onChange={e => setNewAddress(a => ({ ...a, city: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Pincode</label>
                  <input className="form-control" value={newAddress.pincode} onChange={e => setNewAddress(a => ({ ...a, pincode: e.target.value }))} />
                </div>
              </div>
            )}

            {form.type === 'disconnection' ? (
              <div className="form-group">
                <label className="form-label">Reason <span className="required">*</span></label>
                <select className="form-control" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}>
                  <option value="">Select reason…</option>
                  {DISCONNECTION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Reason / Notes</label>
                <textarea className="form-control" rows={3} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !selectedCustomer}>
              <ClipboardList size={14} /> {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Status of a request's GTPL-change reminder (gtplChange on the model), for Admin Lookup's
// GTPL column and the request modal. Renders nothing when there's no reminder.
export function GtplChangeBadge({ gtplChange }) {
  if (!gtplChange?.dueDate) return null;
  if (gtplChange.doneAt) {
    return <span className="badge badge-resolved" title={gtplChange.note || ''}>Done {format(new Date(gtplChange.doneAt), 'd MMM')}</span>;
  }
  const due = new Date(gtplChange.dueDate);
  const overdue = due < startOfDay(new Date());
  return (
    <span className={`badge badge-${overdue ? 'danger' : 'warning'}`} title={gtplChange.note || ''}>
      {overdue ? 'Overdue' : 'Due'} {format(due, 'd MMM')}
    </span>
  );
}

export function RequestActionModal({ request, onClose, onSuccess, startEditing = false }) {
  const [notes, setNotes] = useState('');
  const [acting, setActing] = useState(false);
  const [gtplTicketAutomation, setGtplTicketAutomation] = useState(false);
  const [ticketMode, setTicketMode] = useState('instant');
  const [ticketDate, setTicketDate] = useState(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [ticketTime, setTicketTime] = useState(''); // optional; blank = anytime that day (backend default, midnight)
  const [effectiveDate, setEffectiveDate] = useState(''); // optional; blank = apply immediately on approve
  const Icon = TYPE_ICON[request.type] || ClipboardList;

  // Edit mode — a 'pending' request edits its details (service/plan/package/reason); a
  // 'scheduled' one edits only the effective date (see PUT /:id/edit's split by status).
  const [editing, setEditing] = useState(startEditing);
  const [saving, setSaving] = useState(false);
  const [editServiceType, setEditServiceType] = useState(request.serviceType || 'broadband');
  const [editPlan, setEditPlan] = useState(request.requestedPlan?._id || '');
  const [editPackage, setEditPackage] = useState(request.requestedPackage?._id || '');
  const [editReason, setEditReason] = useState(request.reason || '');
  const [editScheduledFor, setEditScheduledFor] = useState(
    request.scheduledFor ? format(new Date(request.scheduledFor), 'yyyy-MM-dd') : ''
  );
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editGtplDue, setEditGtplDue] = useState(
    request.gtplChange?.dueDate ? format(new Date(request.gtplChange.dueDate), 'yyyy-MM-dd') : ''
  );
  const [editGtplNote, setEditGtplNote] = useState(request.gtplChange?.note || '');
  const [markingGtpl, setMarkingGtpl] = useState(false);

  const setGtplDone = async (done) => {
    setMarkingGtpl(true);
    try {
      const { data } = await serviceRequestAPI.setGtplDone(request._id, done);
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update GTPL change');
    } finally {
      setMarkingGtpl(false);
    }
  };

  useEffect(() => {
    if (!editing || request.type !== 'package_change') return;
    if (editServiceType === 'broadband' && plans.length === 0) {
      broadbandAPI.getPlans({ activeOnly: 'true', limit: 200 }).then(r => setPlans(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
    }
    if (editServiceType === 'catv' && packages.length === 0) {
      catvAPI.getPackages({ activeOnly: 'true', limit: 200 }).then(r => setPackages(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
    }
  }, [editing, editServiceType, request.type, plans.length, packages.length]);

  // A completed request only re-applies live for a CATV package_change on a regular
  // customer (see PUT /:id's comment) — matches the backend condition so the warning shown
  // below is accurate.
  const willReapplyOnSave = request.status === 'completed' && request.type === 'package_change'
    && request.serviceType === 'catv' && (request.customer?.billing?.durationType || 'regular') === 'regular';

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        reason: editReason,
        ...(['disconnection', 'reconnection', 'package_change'].includes(request.type) ? { serviceType: editServiceType } : {}),
        ...(request.type === 'package_change'
          ? (editServiceType === 'broadband' ? { requestedPlan: editPlan } : { requestedPackage: editPackage })
          : {}),
        ...(request.status === 'scheduled' ? { scheduledFor: editScheduledFor } : {}),
        ...(request.type === 'package_change' ? { gtplChangeDue: editGtplDue, gtplChangeNote: editGtplNote } : {}),
      };
      const { data } = await serviceRequestAPI.update(request._id, payload);
      toast.success(data.message || 'Request updated');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update request');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    gtplSaathiAPI.getDisconnectAutomation()
      .then(({ data }) => setGtplTicketAutomation(!!data.data?.enabled))
      .catch(() => {}); // staff without GTPL rights simply never see the ticket prompt
  }, []);

  // Same eligibility rule as CustomerDetailPage's Close Connection dialog
  // (ctvTicketEligible) — a real GTPL-linked, calendar-month-billed CATV account.
  const ticketEligible = gtplTicketAutomation && request.type === 'disconnection' && request.serviceType === 'catv'
    && !!request.customer?.catv?.accountNumber
    && (request.customer?.billing?.durationType || 'regular') === 'regular';

  const act = async (action) => {
    if (action === 'approve' && ticketEligible && ticketMode === 'schedule' && !ticketDate) {
      toast.error('Pick a date for the scheduled disconnect ticket'); return;
    }
    setActing(true);
    try {
      const fn = action === 'approve' ? serviceRequestAPI.approve : serviceRequestAPI.reject;
      const payload = { reviewNotes: notes };
      if (action === 'approve' && effectiveDate) payload.effectiveDate = effectiveDate;
      const { data } = await fn(request._id, payload);
      toast.success(data.message);

      if (action === 'approve' && ticketEligible) {
        try {
          const { data: ticketRes } = await gtplSaathiAPI.createCustomerTicket({
            customerId: request.customer._id,
            complaintType: '382',
            mode: ticketMode,
            scheduledDate: ticketMode === 'schedule'
              ? (ticketTime ? `${ticketDate}T${ticketTime}` : ticketDate)
              : undefined,
          });
          toast.success(ticketMode === 'instant'
            ? `GTPL disconnect ticket raised — Request No ${ticketRes.data.ticketNumber}`
            : `GTPL disconnect ticket scheduled for ${format(new Date(ticketDate), 'dd MMM yyyy')}${ticketTime ? ` at ${ticketTime}` : ''}`);
        } catch (ticketErr) {
          toast.error(ticketErr.response?.data?.message || 'Failed to raise GTPL disconnect ticket');
        }
      }

      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${action} request`);
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3><Icon size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} /> {TYPE_LABEL[request.type]}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {['pending', 'scheduled', 'completed'].includes(request.status) && !editing && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                <Pencil size={12} /> Edit
              </button>
            )}
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <table style={{ width: '100%', fontSize: '.85rem', borderCollapse: 'collapse', marginBottom: 16 }}>
            <tbody>
              <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)', width: 130 }}>Ticket</td><td className="font-mono">{request.ticketNumber}</td></tr>
              <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Customer</td><td>{request.customer?.name} ({request.customer?.mobile})</td></tr>
              {request.serviceType && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Service</td><td style={{ textTransform: 'capitalize' }}>{request.serviceType === 'broadband' ? 'Broadband' : 'CableTV'}</td></tr>
              )}
              {request.requestedPlan && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Requested Plan</td><td>{request.requestedPlan.name} — ₹{request.requestedPlan.price}</td></tr>
              )}
              {request.requestedPackage && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Requested Package</td><td>{request.requestedPackage.name} — ₹{request.requestedPackage.price}</td></tr>
              )}
              {request.newAddress?.line1 && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>New Address</td>
                  <td>{[request.newAddress.line1, request.newAddress.area, request.newAddress.city, request.newAddress.pincode].filter(Boolean).join(', ')}</td></tr>
              )}
              {request.reason && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)', verticalAlign: 'top' }}>Reason</td><td>{request.reason}</td></tr>
              )}
              {request.signature && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)', verticalAlign: 'top' }}>Signature</td>
                  <td><img src={request.signature} alt="Customer signature" style={{ height: 44, background: 'var(--cb-f9fafb)', border: '1px solid var(--border)', borderRadius: 4 }} /></td></tr>
              )}
              <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Status</td>
                <td><span className={`badge badge-${STATUS_BADGE[request.status]}`} style={{ textTransform: 'capitalize' }}>{request.status}</span></td></tr>
              {request.status === 'scheduled' && request.scheduledFor && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Applies On</td>
                  <td style={{ fontWeight: 600 }}>{format(new Date(request.scheduledFor), 'd MMM yyyy')} — regular-customer package changes only take effect on the 1st of the month, per policy</td></tr>
              )}
              {request.gtplChange?.dueDate && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)', verticalAlign: 'top' }}>GTPL Change</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <GtplChangeBadge gtplChange={request.gtplChange} />
                      {!editing && (
                        <button type="button" className="btn btn-secondary btn-sm" disabled={markingGtpl}
                          onClick={() => setGtplDone(!request.gtplChange.doneAt)}>
                          {request.gtplChange.doneAt ? 'Re-open' : <><CheckCircle size={12} /> Mark GTPL done</>}
                        </button>
                      )}
                    </div>
                    {request.gtplChange.note && <div style={{ marginTop: 4 }}>{request.gtplChange.note}</div>}
                    {request.gtplChange.doneAt && (
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        Done {format(new Date(request.gtplChange.doneAt), 'd MMM yyyy')}{request.gtplChange.doneBy?.name ? ` by ${request.gtplChange.doneBy.name}` : ''}
                      </div>
                    )}
                  </td></tr>
              )}
              {request.status !== 'pending' && (
                <>
                  <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Reviewed By</td><td>{request.reviewedBy?.name || '—'}</td></tr>
                  {request.reviewNotes && <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)', verticalAlign: 'top' }}>Review Notes</td><td>{request.reviewNotes}</td></tr>}
                </>
              )}
            </tbody>
          </table>

          {editing && (
            <div style={{ paddingTop: 4, borderTop: '1px solid var(--cb-e5e7eb)' }}>
              {request.status === 'completed' && (
                <div style={{
                  fontSize: '.78rem', padding: '8px 10px', borderRadius: 6, marginBottom: 10,
                  color: willReapplyOnSave ? 'var(--c-1e40af)' : 'var(--c-b45309)', background: willReapplyOnSave ? 'var(--cb-dbeafe)' : 'var(--cb-fef3c7)',
                }}>
                  {willReapplyOnSave
                    ? "Saving will change the customer's CableTV package for real, immediately."
                    : "This only corrects the record. It will not re-apply the change to the customer's account."}
                </div>
              )}
              {['disconnection', 'reconnection', 'package_change'].includes(request.type) && (
                <div className="form-group">
                  <label className="form-label">Service</label>
                  <select className="form-control" value={editServiceType} onChange={e => setEditServiceType(e.target.value)}>
                    <option value="broadband">Broadband</option>
                    <option value="catv">CableTV</option>
                  </select>
                </div>
              )}
              {request.type === 'package_change' && editServiceType === 'broadband' && (
                <div className="form-group">
                  <label className="form-label">Requested Plan <span className="required">*</span></label>
                  <SearchableSelect value={editPlan} onChange={setEditPlan} placeholder="Search plan…"
                    options={plans.map(p => ({ value: p._id, label: `${p.name} — ₹${p.price}` }))} />
                </div>
              )}
              {request.type === 'package_change' && editServiceType === 'catv' && (
                <div className="form-group">
                  <label className="form-label">Requested Package <span className="required">*</span></label>
                  <SearchableSelect value={editPackage} onChange={setEditPackage} placeholder="Search package…"
                    options={packages.map(p => ({ value: p._id, label: `${p.displayName || p.name} — ₹${p.price}` }))} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Reason / Notes</label>
                <textarea className="form-control" rows={3} value={editReason} onChange={e => setEditReason(e.target.value)} />
              </div>
              {request.type === 'package_change' && (
                <div className="form-group">
                  <label className="form-label">GTPL Change Due (optional)</label>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                    {[1, 2, 3].map(n => (
                      <button key={n} type="button" className="btn btn-secondary btn-sm"
                        onClick={() => setEditGtplDue(format(startOfMonth(addMonths(new Date(), n)), 'yyyy-MM-dd'))}>
                        1st of {format(addMonths(new Date(), n), 'MMM')}
                      </button>
                    ))}
                    {editGtplDue && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditGtplDue('')}>Clear</button>
                    )}
                  </div>
                  <input type="date" className="form-control" value={editGtplDue} onChange={e => setEditGtplDue(e.target.value)} />
                  {editGtplDue && (
                    <input className="form-control" style={{ marginTop: 6 }} placeholder="What to change on GTPL, e.g. POWER PLUS to ROYAL HD"
                      value={editGtplNote} onChange={e => setEditGtplNote(e.target.value)} />
                  )}
                  <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    A reminder shown in Admin Lookup only. Nothing is sent to GTPL.
                  </div>
                </div>
              )}
            </div>
          )}

          {editing && request.status === 'scheduled' && (
            <div style={{ paddingTop: 4, borderTop: '1px solid var(--cb-e5e7eb)' }}>
              <div className="form-group">
                <label className="form-label">Effective Date</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                  {[1, 2, 3].map(n => {
                    const monthStart = format(startOfMonth(addMonths(new Date(), n)), 'yyyy-MM-dd');
                    return (
                      <button key={n} type="button" className="btn btn-secondary btn-sm" onClick={() => setEditScheduledFor(monthStart)}>
                        1st of {format(addMonths(new Date(), n), 'MMM')}
                      </button>
                    );
                  })}
                </div>
                <input type="date" className="form-control" min={format(addDays(new Date(), 1), 'yyyy-MM-dd')}
                  value={editScheduledFor} onChange={e => setEditScheduledFor(e.target.value)} />
              </div>
            </div>
          )}

          {!editing && request.status === 'pending' && (
            <div className="form-group">
              <label className="form-label">Review Notes (optional)</label>
              <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          )}

          {!editing && request.status === 'pending' && (
            <div className="form-group">
              <label className="form-label">Effective Date (optional)</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                {[1, 2, 3].map(n => {
                  const monthStart = format(startOfMonth(addMonths(new Date(), n)), 'yyyy-MM-dd');
                  return (
                    <button key={n} type="button" className="btn btn-secondary btn-sm"
                      onClick={() => setEffectiveDate(monthStart)}>
                      1st of {format(addMonths(new Date(), n), 'MMM')}
                    </button>
                  );
                })}
              </div>
              <input type="date" className="form-control" min={format(addDays(new Date(), 1), 'yyyy-MM-dd')}
                value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} />
              <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Leave blank to apply immediately on approval.
                {request.type === 'package_change' && request.serviceType === 'catv'
                  && (request.customer?.billing?.durationType || 'regular') === 'regular'
                  && ' Otherwise a CATV package change defaults to the 1st of next month.'}
              </div>
            </div>
          )}

          {!editing && request.status === 'pending' && ticketEligible && (
            <div style={{ marginTop: 4, paddingTop: 12, borderTop: '1px solid var(--cb-e5e7eb)' }}>
              <div style={{ fontSize: '.8rem', fontWeight: 600, marginBottom: 8 }}>
                GTPL disconnect ticket
              </div>
              <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', marginBottom: 10 }}>
                Approving will raise a "PACKAGE DISCONNECT REQUEST" ticket on GTPL Saathi for account{' '}
                <strong>{request.customer.catv.accountNumber}</strong>.
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.83rem', marginBottom: 8, cursor: 'pointer' }}>
                <input type="radio" name="ticketMode" checked={ticketMode === 'instant'}
                  onChange={() => setTicketMode('instant')} />
                Raise ticket now
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.83rem', marginBottom: ticketMode === 'schedule' ? 6 : 0, cursor: 'pointer' }}>
                <input type="radio" name="ticketMode" checked={ticketMode === 'schedule'}
                  onChange={() => setTicketMode('schedule')} />
                Schedule for a date
              </label>
              {ticketMode === 'schedule' && (
                <div style={{ display: 'flex', gap: 8, marginLeft: 24 }}>
                  <input type="date" className="form-control" style={{ flex: 2 }}
                    value={ticketDate} onChange={e => setTicketDate(e.target.value)} />
                  <input type="time" className="form-control" style={{ flex: 1 }}
                    placeholder="Time (optional)" value={ticketTime} onChange={e => setTicketTime(e.target.value)} />
                </div>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => setEditing(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={saveEdit}>
              <Pencil size={14} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        ) : request.status === 'pending' && (
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" disabled={acting} onClick={() => act('reject')}>
              <XCircle size={14} /> Reject
            </button>
            <button type="button" className="btn btn-primary" disabled={acting} onClick={() => act('approve')}>
              <CheckCircle size={14} /> {acting ? 'Applying...' : 'Approve & Apply'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ServiceRequestsPage() {
  useDarkReady();
  const { viewMode } = useViewMode();
  const isApp = viewMode === 'app' || window.innerWidth < 640;
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [activeRequest, setActiveRequest] = useState(null);
  const [filters, setFilters] = useState({ page: 1, status: isApp ? 'pending' : '', type: '', search: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 20 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const [rd, sd] = await Promise.all([serviceRequestAPI.getAll(params), serviceRequestAPI.getStats()]);
      setRequests(rd.data.data);
      setPagination(rd.data.pagination);
      setStats(sd.data.data);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  if (isApp) {
    const set = (patch) => setFilters(f => ({ ...f, ...patch, page: 1 }));
    const chip = (on, label, onClick) => <button key={label} type="button" className={`mc-chip${on ? ' on' : ''}`} onClick={onClick}>{label}</button>;
    const tabs = [['pending', 'Pending', stats?.pending], ['scheduled', 'Scheduled', stats?.scheduled], ['completed', 'Done', stats?.completed], ['rejected', 'Rejected', stats?.rejected], ['', 'All', stats?.total]];
    return (
      <div>
        <div style={{ position: 'sticky', top: -14, zIndex: 5, margin: '-14px -14px 8px', padding: '8px 14px', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
          <div className="mc-chips" style={{ marginBottom: 8 }}>
            {tabs.map(([v, l, n]) => chip(filters.status === v, n != null ? `${l} ${n}` : l, () => set({ status: v })))}
          </div>
          <div className="mc-chips" style={{ marginBottom: 8 }}>
            {[['', 'Any type'], ...Object.entries(TYPE_LABEL)].map(([v, l]) => chip(filters.type === v, l, () => set({ type: v })))}
          </div>
          <div className="search-input-wrapper">
            <Search size={14} className="search-icon" />
            <input className="search-input" style={{ width: '100%', minHeight: 40 }} placeholder="Ticket, name, mobile…"
              value={filters.search} onChange={e => set({ search: e.target.value })} />
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : requests.length === 0 ? (
          <div className="empty-state"><ClipboardList size={32} /><h3>No requests found</h3></div>
        ) : requests.map(r => {
          const Icon = TYPE_ICON[r.type] || ClipboardList;
          return (
            <div key={r._id} onClick={() => setActiveRequest(r)} role="button"
              style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '9px 12px', marginBottom: 8, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: '.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.customer?.name || '—'}</span>
                <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', flexShrink: 0 }}>{format(new Date(r.createdAt), 'dd MMM, HH:mm')}</span>
              </div>
              <div style={{ fontSize: '.8rem', marginTop: 2 }}>
                <Icon size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{TYPE_LABEL[r.type]}
                <span style={{ color: 'var(--text-muted)' }}> · {r.serviceType === 'broadband' ? 'Broadband' : r.serviceType === 'catv' ? 'CableTV' : '—'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <span className={`badge badge-${STATUS_BADGE[r.status]}`} style={{ textTransform: 'capitalize' }}>{r.status}</span>
                <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{r.ticketNumber} · {r.raisedBy}</span>
              </div>
            </div>
          );
        })}

        {pagination.pages > 1 && (
          <div className="pagination">
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <span style={{ fontSize: '.8rem', padding: '0 8px' }}>{pagination.page} / {pagination.pages}</span>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}

        <div style={{ position: 'sticky', bottom: 14, height: 0, display: 'flex', justifyContent: 'flex-end', pointerEvents: 'none' }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowNew(true)}
            style={{ transform: 'translateY(-100%)', pointerEvents: 'auto', borderRadius: 999, minHeight: 48, padding: '0 18px', boxShadow: '0 4px 14px rgba(0,0,0,.25)' }}>
            <Plus size={16} /> New
          </button>
        </div>

        {showNew && <NewRequestModal onClose={() => setShowNew(false)} onSuccess={() => { setShowNew(false); load(); }} />}
        {activeRequest && (
          <RequestActionModal request={activeRequest} onClose={() => setActiveRequest(null)}
            onSuccess={() => { setActiveRequest(null); load(); }} />
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Service Requests</h2>
          <p className="text-muted text-small">Disconnection, reconnection, package change &amp; relocation requests — {pagination.total || 0} total</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          <Plus size={15} /> New Request
        </button>
      </div>

      {stats && (
        <div className="stats-grid mb-6" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {[
            { label: 'Pending', value: stats.pending, color: 'var(--c-d97706)', bg: 'var(--cb-fef3c7)', icon: Clock },
            { label: 'Scheduled', value: stats.scheduled, color: 'var(--c-d97706)', bg: 'var(--cb-fef3c7)', icon: Clock },
            { label: 'Completed', value: stats.completed, color: 'var(--c-059669)', bg: 'var(--cb-d1fae5)', icon: CheckCircle },
            { label: 'Rejected', value: stats.rejected, color: 'var(--c-dc2626)', bg: 'var(--cb-fee2e2)', icon: XCircle },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-icon" style={{ background: s.bg }}><s.icon size={20} color={s.color} /></div>
              <div className="stat-info"><p>{s.label}</p><h3>{s.value}</h3></div>
            </div>
          ))}
        </div>
      )}

      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search ticket, customer, mobile..."
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        </div>
        <select className="form-control" style={{ width: 140 }} value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
          <option value="superseded">Superseded</option>
        </select>
        <select className="form-control" style={{ width: 180 }} value={filters.type}
          onChange={e => setFilters(f => ({ ...f, type: e.target.value, page: 1 }))}>
          <option value="">All Types</option>
          <option value="disconnection">Disconnection</option>
          <option value="reconnection">Reconnection</option>
          <option value="package_change">Package Change</option>
          <option value="relocation">Relocation</option>
        </select>
        <button className="btn btn-secondary" onClick={() => setFilters({ page: 1, status: '', type: '', search: '' })}>Clear</button>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : requests.length === 0 ? (
            <div className="empty-state"><ClipboardList size={32} /><h3>No requests found</h3></div>
          ) : (
            <table>
              <thead>
                <tr><th>Ticket</th><th>Customer</th><th>Type</th><th>Service</th><th>Status</th><th>Raised</th><th>Created</th><th></th></tr>
              </thead>
              <tbody>
                {requests.map(r => {
                  const Icon = TYPE_ICON[r.type] || ClipboardList;
                  return (
                    <tr key={r._id} style={{ cursor: 'pointer' }} onClick={() => setActiveRequest(r)}>
                      <td className="font-mono" style={{ fontSize: '.78rem' }}>{r.ticketNumber}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.customer?.name}</div>
                        <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{r.customer?.mobile}</div>
                      </td>
                      <td style={{ fontSize: '.82rem' }}>
                        <Icon size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {TYPE_LABEL[r.type]}
                      </td>
                      <td style={{ fontSize: '.82rem', textTransform: 'capitalize' }}>{r.serviceType === 'broadband' ? 'Broadband' : r.serviceType === 'catv' ? 'CableTV' : '—'}</td>
                      <td><span className={`badge badge-${STATUS_BADGE[r.status]}`} style={{ textTransform: 'capitalize' }}>{r.status}</span></td>
                      <td style={{ fontSize: '.82rem', textTransform: 'capitalize' }}>{r.raisedBy}</td>
                      <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{format(new Date(r.createdAt), 'dd/MM/yy HH:mm')}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setActiveRequest(r)}>View</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="pagination">
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <span style={{ fontSize: '.8rem', padding: '0 8px' }}>{pagination.page} / {pagination.pages}</span>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>

      {showNew && <NewRequestModal onClose={() => setShowNew(false)} onSuccess={() => { setShowNew(false); load(); }} />}
      {activeRequest && (
        <RequestActionModal request={activeRequest} onClose={() => setActiveRequest(null)}
          onSuccess={() => { setActiveRequest(null); load(); }} />
      )}
    </div>
  );
}
